import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Store tokens
const saveTokens = (accessToken, refreshToken) => {
  localStorage.setItem('token', accessToken);
  sessionStorage.setItem('token_backup', accessToken);
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
  }
};

const getStoredToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token_backup');
};

const clearTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  sessionStorage.removeItem('token_backup');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => getStoredToken());
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const fetchUser = useCallback(async (authToken) => {
    if (!authToken) {
      setLoading(false);
      setInitialized(true);
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data);
      localStorage.setItem('token', authToken);
      sessionStorage.setItem('token_backup', authToken);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      if (error.response?.status === 401) {
        // Only clear if this token is still the current one (prevent race with login)
        const currentToken = getStoredToken();
        if (!currentToken || currentToken === authToken) {
          clearTokens();
          setToken(null);
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    const storedToken = getStoredToken();
    const storedRefresh = localStorage.getItem('refresh_token');
    
    if (storedToken) {
      setToken(storedToken);
      fetchUser(storedToken);
    } else if (storedRefresh) {
      // Access token gone but refresh exists — try to refresh silently
      (async () => {
        try {
          const res = await axios.post(`${API_URL}/auth/refresh`, { refresh_token: storedRefresh });
          const newAccess = res.data.token;
          const newRefresh = res.data.refresh_token;
          saveTokens(newAccess, newRefresh);
          setToken(newAccess);
          fetchUser(newAccess);
        } catch {
          clearTokens();
          setLoading(false);
          setInitialized(true);
        }
      })();
    } else {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  // Re-fetch user when token changes
  useEffect(() => {
    if (token && initialized) {
      fetchUser(token);
    }
  }, [token, initialized, fetchUser]);

  // Listen for forced logout from api.js interceptor
  useEffect(() => {
    const handleForceLogout = () => {
      // Only logout if there's no fresh token (prevents race with active login)
      const currentToken = getStoredToken();
      if (!currentToken) {
        setToken(null);
        setUser(null);
      }
    };
    window.addEventListener('auth:logout', handleForceLogout);
    return () => window.removeEventListener('auth:logout', handleForceLogout);
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, refresh_token: newRefresh, user: userData } = response.data;
    saveTokens(newToken, newRefresh);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const signup = async (data) => {
    const response = await axios.post(`${API_URL}/auth/signup`, data);
    const { token: newToken, refresh_token: newRefresh, user: userData } = response.data;
    saveTokens(newToken, newRefresh);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    // Revoke token server-side
    try {
      const currentToken = getStoredToken();
      if (currentToken) {
        await axios.post(`${API_URL}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
      }
    } catch { /* best effort */ }
    clearTokens();
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const refreshUser = async () => {
    const currentToken = getStoredToken();
    if (currentToken) {
      setToken(currentToken);
      await fetchUser(currentToken);
    }
  };

  const getAuthHeader = () => ({
    Authorization: `Bearer ${token}`
  });

  const hasToken = !!getStoredToken();

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      initialized,
      login,
      signup,
      logout,
      updateUser,
      refreshUser,
      getAuthHeader,
      isAuthenticated: !!user,
      hasToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};
